import CustomWeb from "@components/atoms/Webview";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import styled, { useTheme } from "styled-components/native";
import EmptyImagePreview from "./EmptyImage/EmptyImagePreview";
import { getShapeValue } from "./EmptyImage/utils";
import OpenReportView from "./OpenReport/OpenReportView";
import { ImagePreviewProps } from "./types";
import Loader from "@components/atoms/Loader";
import { Image as ExpoImage, ImageLoadEventData } from "expo-image";
import { isJPGUrl, isPdfUrl } from "./helpers";
import { MediaType } from "@utils/constants";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
  toDrivePreviewUrl,
} from "@utils/image/imageOptimizer";
import ThreeDWebview from "@components/atoms/ThreeDWebview";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useThreeDProperties } from "../../../../contexts/ThreeDPropertiesContext";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { useIsFocused } from "@react-navigation/native";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { css } from "styled-components/native";
import { addThreeDAdditionalSettingsTypes } from "@utils/product/productValidators";
import {
  ImageDimensions,
  getResizeModeFromDimensions,
} from "@utils/imageAspectRatio";
import { ImageContentFit } from "expo-image";
import { useSelector } from "react-redux";

const { deviceWidth: width } = useIsFoldable();

const ImagePreview = ({
  index,
  isSelected,
  item,
  renderCustomImage,
  resizeMode,
  handleItemClick,
  activeIndex,
  isSmallDeviceWidth,
  deviceWidth,
  applyRadius,
  resizeImage = false,
  extraSmallImage = false,
  shape,
  isExternalView,
  showMediaModel = false,
  isSmallVideoView = false,
  itemID = 0,
  certNum,
  GIACertEnable = false,
  fromStickyHeader = false,
  threeDMedia = [],
  stoneType,
  onRemoveMedia,
  linkedPairChildren,
}: ImagePreviewProps) => {
  const isFocused = useIsFocused();
  const { initStatus } = useOrgSettingsSlice();
  const enableImageOptimizer = useSelector(
    (state: any) => state.globalData?.app_settings?.enable_image_optimizer
  );
  const ImageComponent = ExpoImage;
  const { threeDProperties, setThreeDProperties } = useThreeDProperties();
  const { addThreeDAdditionalSettings } = useJewelryConfiguratorState();
  if (threeDMedia) {
    threeDMedia = addThreeDAdditionalSettings(
      threeDMedia,
      addThreeDAdditionalSettingsTypes.morph
    );
  }
  const threeDMediaObject = {
    three_d_data_type:
      initStatus?.three_d_settings?.three_d_data_type || "webgi",
    functionName: "init",
    params: {
      data: threeDMedia,
      vjson: initStatus?.three_d_settings?.vjson,
    },
  };

  const preFix =
    Platform.OS === "android"
      ? "https://docs.google.com/gview?embedded=true&url="
      : "";
  const theme = useTheme();
  const loadingUrlExternalView = !!(
    item.type === MediaType.PDF &&
    (isPdfUrl(item.link) ||
      showMediaModel ||
      ["om.barakdiamonds.com"].some((domain) => item.link?.includes(domain)))
  );

  const [imageError, setImageError] = useState(item.link === "" ? true : false);
  const TouchableContainerView =
    item.type === MediaType.MODEL_3D
      ? React.Fragment
      : TouchableWithoutFeedback;

  useEffect(() => {
    setImageError(item.link === "");
  }, [item.link]);

  const [loading, setLoading] = useState(false);

  const [imageDimensions, setImageDimensions] =
    useState<ImageDimensions | null>(null);

  const dynamicResizeMode: ImageContentFit = useMemo(() => {
    const isImageType = item.type === MediaType.IMAGE || isJPGUrl(item.link);
    if (!isImageType) {
      return resizeMode ?? "cover";
    }
    if (isJPGUrl(item.link) && item.type === MediaType.PDF) {
      return "contain";
    }
    return enableImageOptimizer
      ? getResizeModeFromDimensions(
          imageDimensions,
          resizeMode ?? "cover",
          theme
        )
      : resizeMode ?? "cover";
  }, [item.type, item.link, imageDimensions, resizeMode]);

  const handleImageLoad = useCallback((event: ImageLoadEventData) => {
    const { width: mWidth, height: mHeight } = event.source;
    if (mWidth && mHeight) {
      setImageDimensions({ width: mWidth, height: mHeight });
    }
  }, []);

  const handleLoadEnd = () => {
    setLoading(false);
  };

  const handleLoadStart = () => {
    setLoading(true);
  };

  const handleError = () => {
    setImageError(true);
    setLoading(false);
    onRemoveMedia?.(index, item.id);
  };

  const isJPGImage = isJPGUrl(item.link);
  const pdpPreset = fromStickyHeader
    ? ImageSizePreset.PDP_STICKY
    : ImageSizePreset.PDP_MAIN;
  const optimizedUri = getOptimizedImageUrl(item.link, pdpPreset);
  const imageComponent = useMemo(() => {
    const fitMode = (
      item.link
        ? isJPGImage && item.type === MediaType.PDF
          ? "contain"
          : dynamicResizeMode
        : "contain"
    ) as "cover" | "contain" | "fill" | "none" | "scale-down";
    return item.type === MediaType.IMAGE || isJPGImage ? (
      !imageError ? (
        <>
          {loading && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          <ImageComponent
            contentFit={fitMode}
            source={getImageSource(optimizedUri, pdpPreset)}
            {...getIOSImageProps()}
            onLoad={handleImageLoad}
            onLoadStart={handleLoadStart}
            onLoadEnd={handleLoadEnd}
            onError={handleError}
            {...(enableImageOptimizer &&
              theme.isLargeScreen && { contentPosition: "top center" })}
            style={[
              fromStickyHeader ? { height: 250, width: 250 } : null,
              !fromStickyHeader && isSmallDeviceWidth
                ? styles.smallImage(
                    deviceWidth,
                    applyRadius,
                    resizeImage,
                    false,
                    theme.isMobile
                  )
                : null,
              !fromStickyHeader && resizeImage
                ? !theme.isMobile
                  ? styles.smallImage(
                      600,
                      applyRadius,
                      resizeImage,
                      true,
                      theme.isMobile
                    )
                  : styles.galleryImage
                : null,
              !fromStickyHeader && !resizeImage && !extraSmallImage
                ? styles.largeImage
                : null,
              {
                backgroundColor: theme.isLargeScreen
                  ? theme["core-02"]
                  : theme["core-01"],
              },
            ]}
          />
        </>
      ) : (
        <EmptyImagePreview
          shape={getShapeValue({ shape })}
          size={extraSmallImage && theme.isMobile ? 100 : 300}
          extraSmallImage={extraSmallImage}
          isExternalView={isExternalView}
          isPair={stoneType === SearchFilterTypeEnum.PAIRS}
        />
      )
    ) : loadingUrlExternalView ? (
      <OpenReportView
        resizeImage={extraSmallImage}
        url={item.link}
        showMediaModel={showMediaModel}
        mediaType={item.type}
        itemID={itemID}
        certNum={certNum}
        GIACertEnable={GIACertEnable}
        linkedPairChildren={linkedPairChildren}
      />
    ) : item.type === MediaType.MODEL_3D ? (
      <Container extraSmallImage={extraSmallImage}>
        <View
          style={{
            flex: 1,
            width: "100%",
            height: "100%",
          }}
        >
          {isFocused && (
            <ThreeDWebview
              isSmallDeviceWidth={isSmallDeviceWidth}
              deviceWidth={deviceWidth}
              resizeImage={resizeImage}
              url={item.link}
              extraSmallImage={false}
              allowScaleScript={false}
              showLoader={false}
              mediaType={item.type}
              videoType={item.video_type}
              isClickable={!fromStickyHeader}
              injectJavaScript={threeDMediaObject}
              iframeKey={
                fromStickyHeader ? "vdb-sticky-iframe" : "vdb-gallery-iframe"
              }
              threeDProperties={threeDProperties}
              setThreeDProperties={setThreeDProperties}
            />
          )}
        </View>
      </Container>
    ) : (
      <Container extraSmallImage={extraSmallImage}>
        <View
          style={{
            width:
              isSmallVideoView &&
              item.type === MediaType.VIDEO &&
              theme.isMobile
                ? "99%"
                : "100%",
            height: "100%",
          }}
        >
          {isFocused && (
            <CustomWeb
              isSmallDeviceWidth={isSmallDeviceWidth}
              deviceWidth={deviceWidth}
              resizeImage={resizeImage}
              url={
                item.type === MediaType.PDF
                  ? preFix + item.link
                  : toDrivePreviewUrl(item.link)
              }
              extraSmallImage={extraSmallImage}
              enableReload={item.type === MediaType.PDF}
              allowScaleScript={false}
              showLoader={true}
              mediaType={item.type}
              videoType={item.video_type}
              isClickable={false}
              errorCallback={handleError}
            />
          )}
        </View>
      </Container>
    );
  }, [
    item.link,
    item.type,
    dynamicResizeMode,
    imageError,
    setImageError,
    loading,
    threeDMediaObject,
    handleImageLoad,
    isFocused,
  ]);

  return (
    <ViewContainer
      resizeImage={resizeImage}
      imageError={imageError}
      fromStickyHeader={fromStickyHeader}
    >
      <TouchableContainerView
        {...(item.type !== MediaType.MODEL_3D && {
          onPress: () => {
            handleItemClick?.(item, activeIndex);
          },
        })}
      >
        <View
          style={
            resizeImage
              ? !theme.isMobile
                ? styles.tabletContainerStyle
                : styles.centerContainerStyle
              : styles.containerStyle
          }
        >
          {renderCustomImage
            ? renderCustomImage(item, index, isSelected)
            : imageComponent}
        </View>
      </TouchableContainerView>
    </ViewContainer>
  );
};

const styles = StyleSheet.create({
  containerStyle: {
    height: "100%",
    width,
  },
  centerContainerStyle: {
    height: "100%",
    width: "100%",
    alignItems: "center",
  },
  largeImage: {
    height: "100%",
    width: "100%",
  },
  tabletContainerStyle: {
    height: "100%",
    width: "100%",
    borderRadius: 20,
  },
  galleryImage: {
    justifyContent: "center",
    alignItems: "center",
  },
  smallImage: (
    deviceWidth: number,
    applyRadius: boolean,
    resizeImage: boolean,
    applyPercent: boolean
  ) => ({
    height: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    width: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    // borderRadius: applyRadius ? 12 : 0,
    paddingBottom: "20px",
    overflow: "hidden",
    borderTopLeftRadius: resizeImage ? 0 : 11,
    borderTopRightRadius: resizeImage ? 0 : 11,
  }),
});

const Container = styled.View<any>`
  flex: 1;
  justify-content: center;
  align-items: center;

  background-color: ${({ theme, extraSmallImage }) =>
    extraSmallImage ? undefined : theme["core-01"]};
  height: 100%;
`;

const ViewContainer = styled.View<{
  resizeImage: boolean;
  imageError: boolean;
  fromStickyHeader: boolean;
}>`
  height: 100%;
  justify-content: flex-start;

  background-color: ${({ theme, resizeImage, imageError, fromStickyHeader }) =>
    resizeImage
      ? imageError && !fromStickyHeader
        ? "transparent"
        : "transparent"
      : theme["core-02"]};

  ${({ resizeImage, theme }) =>
    resizeImage &&
    css`
      margin-left: ${theme.isMobile ? "17px" : "0px"};
      margin-right: ${theme.isMobile ? "17px" : "0px"};
      padding-left: ${theme.isMobile ? "17px" : "0px"};
      padding-right: ${theme.isMobile ? "17px" : "0px"};
    `}
`;

const LoaderContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

export default ImagePreview;
